import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';
import { Notification, NotificationsState } from '../../types';

const initialState: NotificationsState = {
    notifications: [],
    isLoading: false,
    error: null,
};

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/notifications');
            return response.data as Notification[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
        }
    }
);

export const markNotificationRead = createAsyncThunk(
    'notifications/markRead',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await api.patch(`/notifications/${id}/read`);
            return response.data as Notification;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as read');
        }
    }
);

export const markAllNotificationsRead = createAsyncThunk(
    'notifications/markAllRead',
    async (_, { rejectWithValue }) => {
        try {
            await api.patch('/notifications/read-all');
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to mark all as read');
        }
    }
);

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload as any;
                state.notifications = Array.isArray(payload)
                    ? payload
                    : (payload.notifications ?? []);
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Optimistic update: mark single notification read immediately
            .addCase(markNotificationRead.pending, (state, action) => {
                const id = action.meta.arg;
                const n = state.notifications.find(n => n.id === id);
                if (n) n.read_at = new Date().toISOString();
            })
            .addCase(markNotificationRead.fulfilled, (state, action) => {
                const idx = state.notifications.findIndex(n => n.id === action.payload.id);
                if (idx !== -1) state.notifications[idx] = action.payload;
            })
            // Optimistic update: mark all read immediately
            .addCase(markAllNotificationsRead.pending, (state) => {
                const now = new Date().toISOString();
                state.notifications = state.notifications.map(n => ({
                    ...n,
                    read_at: n.read_at ?? now,
                }));
            });
    },
});

export default notificationsSlice.reducer;
